import React, { useEffect, useState } from "react";
import { getHotelInsightsAPI, gethotelinfoIdAPI, getAllCustomerAPI, BASE_URL, getToken } from "../../helpers/api.ts";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
} from "react-bootstrap";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

function HotelInsights() {
  const navigate = useNavigate();
  const [insightsData, setInsightsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hotelDetails, setHotelDetails] = useState(null);
  // ── GST state — mirrors CheckoutModal logic ──────────────────────────────
const [roomGst, setRoomGst] = useState(0);
const [foodGst, setFoodGst] = useState(0);
const [spaGst,  setSpaGst]  = useState(0);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    roomType: "",
    acType: "",
    priceRangeMin: "",
    priceRangeMax: "",
    menuCategory: "",
    spaService: "",
    limit: 10,
  });


  const fetchInsights = async () => {
    try {
      setLoading(true);
      const userString = localStorage.getItem("user");
      const user = userString ? JSON.parse(userString) : {};
      let hotel_id;
      if (user?.role === "RECEPTIONIST") {
        hotel_id = user?.hotel?.hotel_id;
      } else {
        hotel_id = user?.hotel_id;
      }

      const filterData = {};
      Object.keys(filters).forEach((key) => {
        if (
          filters[key] !== "" &&
          filters[key] !== null &&
          filters[key] !== undefined
        ) {
          filterData[key] = filters[key];
        }
      });

      const response = await getHotelInsightsAPI(hotel_id, filterData);
      setInsightsData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching insights:", error);
      setLoading(false);
    }
  };

  const fetchHotelDetails = async () => {
    try {
      const userString = localStorage.getItem("user");
      const user = userString ? JSON.parse(userString) : {};
      let hotel_id;
      if (user?.role === "RECEPTIONIST") {
        hotel_id = user?.hotel?.hotel_id;
      } else {
        hotel_id = user?.hotel_id;
      }

      if (hotel_id) {
        const response = await gethotelinfoIdAPI(hotel_id);
        if (response?.success) {
          setHotelDetails(response.data);
        }
      }
    } catch (error) {
      console.error("Error fetching hotel details:", error);
    }
  };

 const fetchHotelGst = async () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const hotel_id =
      user?.role === "RECEPTIONIST"
        ? user?.hotel?.hotel_id
        : user?.hotel_id;

    if (!hotel_id) return;

    const response = await fetch(`${BASE_URL}/hotel/getHotelDetails`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ hotel_id }),
    });

    const data = await response.json();
    if (data?.success && data?.data) {
      const defaultGst = parseFloat(data.data.gst_percentage) || 0;
      // Set all three to hotel default first
      setRoomGst(defaultGst);
      setFoodGst(defaultGst);
      setSpaGst(defaultGst);

      // Then override with per-category GST if billing records have them
      const breakdown = data.data?.tax_category_breakdown || [];
      breakdown.forEach((b) => {
        const pct = parseFloat(b.gst_percentage) || 0;
        if (!pct) return;
        if (b.category === "Room Charges") setRoomGst(pct);
        if (b.category === "Food Charges") setFoodGst(pct);
        if (b.category === "Spa Charges")  setSpaGst(pct);
      });
    }
  } catch (err) {
    console.warn("Could not fetch hotel GST:", err);
  }
};


  useEffect(() => {
  fetchInsights();
  fetchHotelDetails();
  fetchHotelGst();
}, []);




  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    fetchInsights();
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      roomType: "",
      acType: "",
      priceRangeMin: "",
      priceRangeMax: "",
      menuCategory: "",
      spaService: "",
      limit: 10,
    });
    setTimeout(() => {
      fetchInsights();
    }, 100);
  };

  // ─── Helper: round to 2 decimal places ───────────────────────────────────
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  // ─── Helper: compute GST amount from base ────────────────────────────────
  const gstAmt = (base, pct) => r2((base * pct) / 100);

  // ─── Excel cell style helpers ────────────────────────────────────────────
  const headerStyle = (rgb = "1F4E79") => ({
    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
    fill: { patternType: "solid", fgColor: { rgb } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    },
  });

  const totalStyle = (rgb = "D9E1F2") => ({
    font: { bold: true, sz: 10 },
    fill: { patternType: "solid", fgColor: { rgb } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "medium", color: { rgb: "000000" } },
      bottom: { style: "medium", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    },
  });

  const dataStyle = (rgb = null) => ({
    font: { sz: 10 },
    fill: rgb ? { patternType: "solid", fgColor: { rgb } } : undefined,
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "hair", color: { rgb: "CCCCCC" } },
      bottom: { style: "hair", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    },
  });

  const grandTotalStyle = () => ({
    font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: "C00000" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "medium", color: { rgb: "000000" } },
      bottom: { style: "medium", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    },
  });

  // ─── Apply styles to a worksheet range ───────────────────────────────────
  const applyStyleToRow = (ws, rowIdx, colCount, style) => {
    for (let c = 0; c < colCount; c++) {
      const addr = XLSX.utils.encode_cell({ r: rowIdx, c });
      if (!ws[addr]) ws[addr] = { v: "", t: "s" };
      ws[addr].s = style;
    }
  };

  const applyStyleToCell = (ws, rowIdx, colIdx, style) => {
    const addr = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
    if (!ws[addr]) ws[addr] = { v: "", t: "s" };
    ws[addr].s = style;
  };

  // ─── Main Excel export ───────────────────────────────────────────────────
 
const downloadExcel = () => {
  if (!insightsData) return;

  // ── Per-category GST from actual billing records ──────────────────────────
  // taxInsights.categoryBreakdown = [
  //   { category: "Room Charges",  base_amount, tax_amount, gst_percentage, ... },
  //   { category: "Food Charges",  ... },
  //   { category: "Spa Charges",   ... },
  // ]
  const breakdown = insightsData?.taxInsights?.categoryBreakdown || [];
  const findGst = (label, fallback) => {
    const row = breakdown.find((b) => b.category === label);
    const pct = parseFloat(row?.gst_percentage ?? NaN);
    return isNaN(pct) || pct === 0 ? parseFloat(fallback || 0) : pct;
  };

  // Flat hotel GST used ONLY as fallback when billing records are absent
  const hotelGstFallback = Number(hotelDetails?.gst_percentage || 0);
// ✅ Use billing record GST first, fall back to hotel default
const roomGstPct = findGst("Room Charges", roomGst);
const foodGstPct = findGst("Food Charges", foodGst);
const spaGstPct  = findGst("Spa Charges", spaGst);

  const hotelName  = hotelDetails?.hotel_name || "Hotel";
  const gstNo      = hotelDetails?.gst_number || "";
  const exportDate = new Date().toLocaleDateString("en-IN");
  const dateRange  =
    filters.startDate && filters.endDate
      ? `${filters.startDate} to ${filters.endDate}`
      : "All Dates";

  const workbook = XLSX.utils.book_new();

  // ─── Shared helpers (unchanged from original) ───────────────────────────
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const gstAmt = (base, pct) => r2((base * pct) / 100);

  const headerStyle = (rgb = "1F4E79") => ({
    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
    fill: { patternType: "solid", fgColor: { rgb } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    },
  });

  const totalStyle = (rgb = "D9E1F2") => ({
    font: { bold: true, sz: 10 },
    fill: { patternType: "solid", fgColor: { rgb } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "medium", color: { rgb: "000000" } },
      bottom: { style: "medium", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    },
  });

  const dataStyle = (rgb = null) => ({
    font: { sz: 10 },
    fill: rgb ? { patternType: "solid", fgColor: { rgb } } : undefined,
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "hair", color: { rgb: "CCCCCC" } },
      bottom: { style: "hair", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    },
  });

  const grandTotalStyle = () => ({
    font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: "C00000" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "medium", color: { rgb: "000000" } },
      bottom: { style: "medium", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    },
  });

  const applyStyleToRow = (ws, rowIdx, colCount, style) => {
    for (let c = 0; c < colCount; c++) {
      const addr = XLSX.utils.encode_cell({ r: rowIdx, c });
      if (!ws[addr]) ws[addr] = { v: "", t: "s" };
      ws[addr].s = style;
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // SHEET 1 — ROOMS  (uses roomGstPct)
  // ════════════════════════════════════════════════════════════════════════
  {
    const ROOM_COLS = [
      { header: "Sr No",                          width: 7  },
      { header: "Room Number",                    width: 14 },
      { header: "Room Type",                      width: 14 },
      { header: "AC / Non-AC",                    width: 12 },
      { header: "Tariff (₹)",                     width: 13 },
      { header: "Bookings",                       width: 10 },
      { header: "Base Revenue (₹)",               width: 16 },
      { header: `Room GST (${roomGstPct}%)`,      width: 14 },  // ← actual room GST
      { header: "GST Amount (₹)",                 width: 14 },
      { header: "Total Revenue (₹)",              width: 16 },
    ];

    const aoa = [];
    aoa.push([hotelName + " — Room Insights Report"]);
    aoa.push([`GST No: ${gstNo}   |   Date Range: ${dateRange}   |   Export Date: ${exportDate}`]);
    aoa.push([]);
    aoa.push(ROOM_COLS.map((c) => c.header));

    let totBookings = 0, totBase = 0, totGst = 0, totTotal = 0;
    insightsData.topRooms.forEach((room, idx) => {
      const base  = r2(room.tariff * room.booking_count);
      const gst   = gstAmt(base, roomGstPct);   // ← per-category GST
      const total = r2(base + gst);
      totBookings += room.booking_count;
      totBase     += base;
      totGst      += gst;
      totTotal    += total;
      aoa.push([
        idx + 1,
        room.room_number,
        room.room_type,
        room.ac_no_ac,
        room.tariff,
        room.booking_count,
        base,
        `${roomGstPct}%`,
        gst,
        total,
      ]);
    });
    aoa.push(["TOTAL", "", "", "", "", totBookings, r2(totBase), "", r2(totGst), r2(totTotal)]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const numCols = ROOM_COLS.length;
    ws["!cols"] = ROOM_COLS.map((c) => ({ wch: c.width }));
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
    ];
    applyStyleToRow(ws, 0, numCols, { font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "1F4E79" } }, alignment: { horizontal: "center", vertical: "center" } });
    applyStyleToRow(ws, 1, numCols, { font: { sz: 10, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "2E75B6" } }, alignment: { horizontal: "center" } });
    applyStyleToRow(ws, 3, numCols, headerStyle("2E75B6"));
    for (let r = 4; r < 4 + insightsData.topRooms.length; r++) {
      applyStyleToRow(ws, r, numCols, dataStyle((r % 2 === 0) ? "EBF3FB" : null));
    }
    applyStyleToRow(ws, 4 + insightsData.topRooms.length, numCols, totalStyle("BDD7EE"));
    XLSX.utils.book_append_sheet(workbook, ws, "Rooms");
  }

  // ════════════════════════════════════════════════════════════════════════
  // SHEET 2 — RESTAURANT  (uses foodGstPct — per HotelMenu.gst)
  // ════════════════════════════════════════════════════════════════════════
  {
    const MENU_COLS = [
      { header: "Sr No",                          width: 7  },
      { header: "Dish Name",                      width: 22 },
      { header: "Category",                       width: 15 },
      { header: "Price (₹)",                      width: 12 },
      { header: "Orders",                         width: 10 },
      { header: "Total Qty",                      width: 10 },
      { header: "Base Revenue (₹)",               width: 16 },
      { header: `Food GST (${foodGstPct}%)`,      width: 14 },  // ← actual food GST
      { header: "GST Amount (₹)",                 width: 14 },
      { header: "Total Revenue (₹)",              width: 16 },
    ];

    const aoa = [];
    aoa.push([hotelName + " — Restaurant Insights Report"]);
    aoa.push([`GST No: ${gstNo}   |   Date Range: ${dateRange}   |   Export Date: ${exportDate}`]);
    aoa.push([]);
    aoa.push(MENU_COLS.map((c) => c.header));

    let totOrders = 0, totQty = 0, totBase = 0, totGst = 0, totTotal = 0;
    insightsData.topMenuItems.forEach((item, idx) => {
      // Prefer item-level GST if available, fall back to category-level
      const itemGstPct = item.gst != null ? parseFloat(item.gst) : foodGstPct;
      const base  = r2(item.price * item.order_count);
      const gst   = gstAmt(base, itemGstPct);   // ← per-item GST (falls back to category)
      const total = r2(base + gst);
      totOrders += item.order_count;
      totQty    += item.total_quantity;
      totBase   += base;
      totGst    += gst;
      totTotal  += total;
      aoa.push([
        idx + 1,
        item.name,
        item.category,
        item.price,
        item.order_count,
        item.total_quantity,
        base,
        `${itemGstPct}%`,
        gst,
        total,
      ]);
    });
    aoa.push(["TOTAL", "", "", "", totOrders, totQty, r2(totBase), "", r2(totGst), r2(totTotal)]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const numCols = MENU_COLS.length;
    ws["!cols"] = MENU_COLS.map((c) => ({ wch: c.width }));
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
    ];
    applyStyleToRow(ws, 0, numCols, { font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "375623" } }, alignment: { horizontal: "center", vertical: "center" } });
    applyStyleToRow(ws, 1, numCols, { font: { sz: 10, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "548235" } }, alignment: { horizontal: "center" } });
    applyStyleToRow(ws, 3, numCols, headerStyle("548235"));
    for (let r = 4; r < 4 + insightsData.topMenuItems.length; r++) {
      applyStyleToRow(ws, r, numCols, dataStyle((r % 2 === 0) ? "E2EFDA" : null));
    }
    applyStyleToRow(ws, 4 + insightsData.topMenuItems.length, numCols, totalStyle("A9D18E"));
    XLSX.utils.book_append_sheet(workbook, ws, "Restaurant");
  }

  // ════════════════════════════════════════════════════════════════════════
  // SHEET 3 — SPA  (uses spaGstPct — per SpaService.gst)
  // ════════════════════════════════════════════════════════════════════════
  {
    const SPA_COLS = [
      { header: "Sr No",                          width: 7  },
      { header: "Service Name",                   width: 24 },
      { header: "Duration (mins)",                width: 15 },
      { header: "Price (₹)",                      width: 12 },
      { header: "Appointments",                   width: 14 },
      { header: "Base Revenue (₹)",               width: 16 },
      { header: `Spa GST (${spaGstPct}%)`,        width: 13 },  // ← actual spa GST
      { header: "GST Amount (₹)",                 width: 14 },
      { header: "Total Revenue (₹)",              width: 16 },
    ];

    const aoa = [];
    aoa.push([hotelName + " — Spa Insights Report"]);
    aoa.push([`GST No: ${gstNo}   |   Date Range: ${dateRange}   |   Export Date: ${exportDate}`]);
    aoa.push([]);
    aoa.push(SPA_COLS.map((c) => c.header));

    let totAppts = 0, totBase = 0, totGst = 0, totTotal = 0;
    insightsData.topSpaServices.forEach((svc, idx) => {
      // Prefer service-level GST if available, fall back to category-level
      const svcGstPct = svc.gst != null ? parseFloat(svc.gst) : spaGstPct;
      const base  = r2(parseFloat(svc.price) * svc.appointment_count);
      const gst   = gstAmt(base, svcGstPct);   // ← per-service GST
      const total = r2(base + gst);
      totAppts += svc.appointment_count;
      totBase  += base;
      totGst   += gst;
      totTotal += total;
      aoa.push([
        idx + 1,
        svc.name,
        svc.duration,
        parseFloat(svc.price),
        svc.appointment_count,
        base,
        `${svcGstPct}%`,
        gst,
        total,
      ]);
    });
    aoa.push(["TOTAL", "", "", "", totAppts, r2(totBase), "", r2(totGst), r2(totTotal)]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const numCols = SPA_COLS.length;
    ws["!cols"] = SPA_COLS.map((c) => ({ wch: c.width }));
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
    ];
    applyStyleToRow(ws, 0, numCols, { font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "7030A0" } }, alignment: { horizontal: "center", vertical: "center" } });
    applyStyleToRow(ws, 1, numCols, { font: { sz: 10, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "9933CC" } }, alignment: { horizontal: "center" } });
    applyStyleToRow(ws, 3, numCols, headerStyle("9933CC"));
    for (let r = 4; r < 4 + insightsData.topSpaServices.length; r++) {
      applyStyleToRow(ws, r, numCols, dataStyle((r % 2 === 0) ? "F4EAFB" : null));
    }
    applyStyleToRow(ws, 4 + insightsData.topSpaServices.length, numCols, totalStyle("C8A0D8"));
    XLSX.utils.book_append_sheet(workbook, ws, "Spa");
  }

  // ════════════════════════════════════════════════════════════════════════
  // SHEET 4 — GRAND SUMMARY
  // Uses taxInsights.summary for authoritative totals (from actual billings),
  // with computed fallback when billing records don't exist yet.
  // ════════════════════════════════════════════════════════════════════════
  {
    // Prefer taxInsights totals (calculated from actual billing DB records)
    const ti = insightsData?.taxInsights;
    const tiBreakdown = ti?.categoryBreakdown || [];
    const getRow = (label) => tiBreakdown.find((b) => b.category === label) || {};

    const roomRow = getRow("Room Charges");
    const foodRow = getRow("Food Charges");
    const spaRow  = getRow("Spa Charges");

    // If taxInsights has real data use it, otherwise compute from topRooms/topMenuItems/topSpaServices
    const hasRealData = (row) => Number(row?.total_amount || 0) > 0;

    // Rooms
    let roomBase, roomGst, roomBookings;
    if (hasRealData(roomRow)) {
      roomBase     = r2(roomRow.base_amount);
      roomGst      = r2(roomRow.tax_amount);
      roomBookings = insightsData.topRooms.reduce((s, r) => s + r.booking_count, 0);
    } else {
      roomBookings = 0;
      roomBase = 0; roomGst = 0;
      insightsData.topRooms.forEach((room) => {
        const b = r2(room.tariff * room.booking_count);
        roomBase     += b;
        roomGst      += gstAmt(b, roomGstPct);
        roomBookings += room.booking_count;
      });
      roomBase = r2(roomBase); roomGst = r2(roomGst);
    }

    // Food
    let menuBase, menuGst, menuOrders, menuQty;
    if (hasRealData(foodRow)) {
      menuBase   = r2(foodRow.base_amount);
      menuGst    = r2(foodRow.tax_amount);
      menuOrders = insightsData.topMenuItems.reduce((s, i) => s + i.order_count, 0);
      menuQty    = insightsData.topMenuItems.reduce((s, i) => s + i.total_quantity, 0);
    } else {
      menuOrders = 0; menuQty = 0; menuBase = 0; menuGst = 0;
      insightsData.topMenuItems.forEach((item) => {
        const itemGstPct = item.gst != null ? parseFloat(item.gst) : foodGstPct;
        const b = r2(item.price * item.order_count);
        menuBase   += b;
        menuGst    += gstAmt(b, itemGstPct);
        menuOrders += item.order_count;
        menuQty    += item.total_quantity;
      });
      menuBase = r2(menuBase); menuGst = r2(menuGst);
    }

    // Spa
    let spaBase, spaGst, spaAppts;
    if (hasRealData(spaRow)) {
      spaBase  = r2(spaRow.base_amount);
      spaGst   = r2(spaRow.tax_amount);
      spaAppts = insightsData.topSpaServices.reduce((s, sv) => s + sv.appointment_count, 0);
    } else {
      spaAppts = 0; spaBase = 0; spaGst = 0;
      insightsData.topSpaServices.forEach((svc) => {
        const svcGstPct = svc.gst != null ? parseFloat(svc.gst) : spaGstPct;
        const b = r2(parseFloat(svc.price) * svc.appointment_count);
        spaBase  += b;
        spaGst   += gstAmt(b, svcGstPct);
        spaAppts += svc.appointment_count;
      });
      spaBase = r2(spaBase); spaGst = r2(spaGst);
    }

    const grandBase  = r2(roomBase + menuBase + spaBase);
    const grandGst   = r2(roomGst  + menuGst  + spaGst);
    const grandTotal = r2(grandBase + grandGst);

    const SUMMARY_COLS = [
      { header: "Category",             width: 22 },
      { header: "Count / Bookings",     width: 18 },
      { header: "Base Revenue (₹)",     width: 18 },
      { header: "GST %",                width: 12 },  // ← shows actual per-category %
      { header: "GST Amount (₹)",       width: 16 },
      { header: "Total Revenue (₹)",    width: 18 },
    ];

    const aoa = [];
    aoa.push([hotelName + " — Hotel Insights Summary"]);
    aoa.push([`GST No: ${gstNo}   |   Date Range: ${dateRange}   |   Export Date: ${exportDate}`]);
    aoa.push([]);
    aoa.push(SUMMARY_COLS.map((c) => c.header));

    aoa.push(["Rooms",      roomBookings, roomBase, `${roomGstPct}%`, roomGst, r2(roomBase + roomGst)]);
    aoa.push(["Restaurant", menuOrders,   menuBase, `${foodGstPct}%`, menuGst, r2(menuBase + menuGst)]);
    aoa.push(["Spa",        spaAppts,     spaBase,  `${spaGstPct}%`,  spaGst,  r2(spaBase  + spaGst)]);
    aoa.push([]);
    aoa.push(["GRAND TOTAL", roomBookings + menuOrders + spaAppts, grandBase, "—", grandGst, grandTotal]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const numCols = SUMMARY_COLS.length;
    ws["!cols"] = SUMMARY_COLS.map((c) => ({ wch: c.width }));
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
    ];
    applyStyleToRow(ws, 0, numCols, { font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "1F4E79" } }, alignment: { horizontal: "center", vertical: "center" } });
    applyStyleToRow(ws, 1, numCols, { font: { sz: 10, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "2E75B6" } }, alignment: { horizontal: "center" } });
    applyStyleToRow(ws, 3, numCols, headerStyle("1F4E79"));
    applyStyleToRow(ws, 4, numCols, dataStyle("BDD7EE"));  // Rooms  — blue
    applyStyleToRow(ws, 5, numCols, dataStyle("E2EFDA"));  // Food   — green
    applyStyleToRow(ws, 6, numCols, dataStyle("F4EAFB"));  // Spa    — purple
    applyStyleToRow(ws, 8, numCols, grandTotalStyle());    // Grand total — red
    ws["!rows"] = [{ hpt: 30 }, { hpt: 18 }, { hpt: 6 }, { hpt: 22 }, { hpt: 20 }, { hpt: 20 }, { hpt: 20 }, { hpt: 6 }, { hpt: 24 }];
    XLSX.utils.book_append_sheet(workbook, ws, "Summary");
  }

  // ════════════════════════════════════════════════════════════════════════
  // SHEET 5 — APPLIED FILTERS (unchanged)
  // ════════════════════════════════════════════════════════════════════════
  {
    const activeFilters = Object.entries(filters)
      .filter(([, v]) => v !== "" && v !== null && v !== undefined)
      .map(([key, value]) => ({
        Filter: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1"),
        Value: String(value),
      }));

    if (activeFilters.length > 0) {
      const filterSheet = XLSX.utils.json_to_sheet(activeFilters);
      filterSheet["!cols"] = [{ wch: 22 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(workbook, filterSheet, "Applied Filters");
    }
  }

  const timestamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `HotelInsights_${hotelName.replace(/\s+/g, "_")}_${timestamp}.xlsx`);
};

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  if (!insightsData) {
    return <div className="text-center p-4">No data available</div>;
  }

  return (
    <Container fluid className="p-4 mt-5">
      <div className="d-flex justify-content-between align-items-center my-4">
        <h1 className="mb-0">Hotel Insights Dashboard</h1>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={() => navigate("/dashboard")}>
            Back
          </Button>
          <Button variant="success" onClick={downloadExcel}>
            <download /> Download Excel
          </Button>
        </div>
      </div>

      {/* Filter Section */}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title as="h5" className="mb-4">Filters</Card.Title>
          <Row className="g-3">
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange("startDate", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange("endDate", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>Room Type</Form.Label>
                <Form.Select
                  value={filters.roomType}
                  onChange={(e) => handleFilterChange("roomType", e.target.value)}
                >
                  <option value="">All Room Types</option>
                  <option value="SINGLE">Single</option>
                  <option value="DOUBLE">Double</option>
                  <option value="SUITE">Suite</option>
                  <option value="DORMETORY">Dormitory</option>
                  <option value="BANQUET HALL">Banquet Hall</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>Restaurant Category</Form.Label>
                <Form.Select
                  value={filters.menuCategory}
                  onChange={(e) => handleFilterChange("menuCategory", e.target.value)}
                >
                  <option value="">All Categories</option>
                  <option value="Starter">Starter</option>
                  <option value="Main Course">Main Course</option>
                  <option value="Dessert">Dessert</option>
                  <option value="Beverage">Beverage</option>
                  <option value="Breakfast">Breakfast</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>Spa Service</Form.Label>
                <Form.Select
                  value={filters.spaService}
                  onChange={(e) => handleFilterChange("spaService", e.target.value)}
                >
                  <option value="">All Spa Services</option>
                  {insightsData?.topSpaServices?.map((service, index) => (
                    <option key={index} value={service.name}>{service.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-3">
            <Button variant="success" onClick={handleApplyFilters} disabled={loading}>
              {loading ? "Loading..." : "Apply Filters"}
            </Button>
            <Button variant="outline-secondary" onClick={handleClearFilters} disabled={loading}>
              Clear Filters
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-4 my-3">
        {/* Top Rooms */}
        <Col xs={12}>
          <Card>
            <Card.Body>
              <Card.Title as="h5" className="mb-4">Top Performing Rooms</Card.Title>
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={insightsData.topRooms}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="room_number" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="booking_count" fill="#0d6efd" name="Booking Count" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4">
                <Table responsive striped bordered hover>
                  <thead>
                    <tr>
                      <th>Room Number</th>
                      <th>Type</th>
                      <th>AC/Non-AC</th>
                      <th>Tariff</th>
                      <th>Bookings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {insightsData.topRooms.map((room) => (
                      <tr key={room.room_number}>
                        <td>{room.room_number}</td>
                        <td>{room.room_type}</td>
                        <td>{room.ac_no_ac}</td>
                        <td>₹{room.tariff}</td>
                        <td>{room.booking_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Top Menu Items */}
        <Col xs={12} md={6}>
          <Card>
            <Card.Body>
              <Card.Title as="h5" className="mb-4">Popular Menu Items</Card.Title>
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {insightsData.topMenuItems.map((item, index) => (
                    <tr key={index}>
                      <td>{item.name}</td>
                      <td>{item.category}</td>
                      <td>₹{item.price}</td>
                      <td>{item.order_count}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Top Spa Services */}
        <Col xs={12} md={6}>
          <Card>
            <Card.Body>
              <Card.Title as="h5" className="mb-4">Popular Spa Services</Card.Title>
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Duration</th>
                    <th>Price</th>
                    <th>Appointments</th>
                  </tr>
                </thead>
                <tbody>
                  {insightsData.topSpaServices.map((service, index) => (
                    <tr key={index}>
                      <td>{service.name}</td>
                      <td>{service.duration} mins</td>
                      <td>₹{service.price}</td>
                      <td>{service.appointment_count}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default HotelInsights;
